import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Item, CartItemSelection } from '../../types';
import { formatCurrency, uuidv4 } from '../../../../../../../utils/functions';
import { FaHashtag, FaRulerHorizontal, FaDollarSign } from 'react-icons/fa6';
import './GranelModal.scss';

interface GranelModalProps {
  show: boolean;
  onHide: () => void;
  item: Item | null;
  editingCartItem?: CartItemSelection | null;
  onAddToCart: (cartItem: CartItemSelection) => void;
  clientIndex: number;
}

interface UnitConversion {
  name: string;
  acronym: string;
  factor: number;
}

// Unit conversion mappings based on the unidad_medida_ids table
const UNIT_CONVERSIONS: {
  weight: Record<number, UnitConversion>;
  volume: Record<number, UnitConversion>;
} = {
  // Weight units (kg, g, oz)
  weight: {
    1: { name: 'Kilogramos', acronym: 'kg', factor: 1000 }, // kg to grams
    2: { name: 'Gramos', acronym: 'g', factor: 1 }, // base unit grams
    4: { name: 'Onzas', acronym: 'oz', factor: 28.35 }, // oz to grams
  },
  // Volume units (L, ml, fl oz)
  volume: {
    3: { name: 'Litros', acronym: 'L', factor: 1000 }, // L to ml
    5: { name: 'Mililitros', acronym: 'ml', factor: 1 }, // base unit ml
    15: { name: 'Onza líquida (US)', acronym: 'fl oz', factor: 29.5735 }, // fl oz to ml
  }
};

const GranelModal: React.FC<GranelModalProps> = ({
  show,
  onHide,
  item,
  editingCartItem,
  onAddToCart,
  clientIndex
}) => {
  const [quantity, setQuantity] = useState<string>('');
  const [selectedUnitId, setSelectedUnitId] = useState<number>(0);
  const [calculatedPrice, setCalculatedPrice] = useState<number>(0);

  // Determine unit type and available units based on item's unidad_medida_id
  const getUnitTypeAndOptions = (unidadMedidaId: number) => {
    // Check if it's a weight unit
    if ([1, 2, 4].includes(unidadMedidaId)) {
      return {
        type: 'weight',
        options: [1, 2, 4], // kg, g, oz
        baseUnit: 2 // grams
      };
    }
    // Check if it's a volume unit
    if ([3, 5, 15].includes(unidadMedidaId)) {
      return {
        type: 'volume',
        options: [3, 5, 15], // L, ml, fl oz
        baseUnit: 5 // ml
      };
    }
    return null;
  };

  // Initialize modal state
  useEffect(() => {
    if (show && item) {
      if (editingCartItem && editingCartItem.is_granel) {
        // When editing, we need to convert from base units back to display units
        // The stored quantity is in base units (grams/ml), but we need to show it in the original selected unit
        
        const unitTypeAndOptions = getUnitTypeAndOptions(item.unidad_medida_id || 0);
        if (unitTypeAndOptions) {
          const conversions = unitTypeAndOptions.type === 'weight' 
            ? UNIT_CONVERSIONS.weight 
            : UNIT_CONVERSIONS.volume;
            
          // Extract original display info from the item name
          // Format: "Item Name (1.5 kg)" -> extract "1.5" and find corresponding unit
          const match = editingCartItem.item_name.match(/\(([0-9.]+)\s*([a-zA-Z]+)\)$/);
          if (match) {
            const originalQuantity = match[1];
            const originalUnitAcronym = match[2];
            
            // Find the unit ID that matches the acronym
            let originalUnitId = item.unidad_medida_id || 0;
            for (const [unitId, unitInfo] of Object.entries(conversions)) {
              if (unitInfo.acronym === originalUnitAcronym) {
                originalUnitId = parseInt(unitId);
                break;
              }
            }
            
            setQuantity(originalQuantity);
            setSelectedUnitId(originalUnitId);
          } else {
            // Fallback: assume it's already in base units
            setQuantity(editingCartItem.quantity.toString());
            setSelectedUnitId(unitTypeAndOptions.baseUnit);
          }
        }
      } else {
        // New granel item
        setQuantity('');
        setSelectedUnitId(item.unidad_medida_id || 0);
      }
    }
  }, [show, item, editingCartItem]);

  // Calculate price based on quantity and selected unit
  useEffect(() => {
    if (!item || !quantity || !selectedUnitId) {
      setCalculatedPrice(0);
      return;
    }

    const unitTypeAndOptions = getUnitTypeAndOptions(item.unidad_medida_id || 0);
    if (!unitTypeAndOptions) {
      setCalculatedPrice(0);
      return;
    }

    const conversions = unitTypeAndOptions.type === 'weight' 
      ? UNIT_CONVERSIONS.weight 
      : UNIT_CONVERSIONS.volume;
    
    const itemUnit = conversions[item.unidad_medida_id as keyof typeof conversions];
    const selectedUnit = conversions[selectedUnitId as keyof typeof conversions];
    
    if (!itemUnit || !selectedUnit) {
      setCalculatedPrice(0);
      return;
    }

    // Convert quantity to item's base unit for price calculation
    const quantityInItemUnit = (parseFloat(quantity) * selectedUnit.factor) / itemUnit.factor;
    const price = quantityInItemUnit * item.price;
    
    setCalculatedPrice(Math.round(price * 100) / 100); // Round to 2 decimals
  }, [quantity, selectedUnitId, item]);

  const handleQuantityChange = (value: string) => {
    // Only allow numbers and one decimal point
    const sanitized = value.replace(/[^\d.]/g, '');
    
    // Prevent multiple decimal points
    const parts = sanitized.split('.');
    if (parts.length > 2) return;
    
    setQuantity(sanitized);
  };

  const handleConfirm = () => {
    if (!item || !quantity || !selectedUnitId || calculatedPrice <= 0) {
      return;
    }

    const unitTypeAndOptions = getUnitTypeAndOptions(item.unidad_medida_id || 0);
    if (!unitTypeAndOptions) return;

    const conversions = unitTypeAndOptions.type === 'weight' 
      ? UNIT_CONVERSIONS.weight 
      : UNIT_CONVERSIONS.volume;
    
    const selectedUnit = conversions[selectedUnitId as keyof typeof conversions];
    const itemUnit = conversions[item.unidad_medida_id as keyof typeof conversions];
    
    if (!selectedUnit || !itemUnit) return;

    // Convert the entered quantity to the base unit (grams or ml) as integer
    // Example: If user enters 2.5 kg, convert to 2500 grams
    const quantityInBaseUnit = Math.round(parseFloat(quantity) * selectedUnit.factor);

    // Get the base unit info for storage
    const baseUnit = conversions[unitTypeAndOptions.baseUnit as keyof typeof conversions];
    
    // For display in cart, show in the selected unit
    const displayQuantity = parseFloat(quantity);
    const displayAcronym = selectedUnit.acronym;

    // Create modified item name with display unit
    const itemNameWithUnit = `${item.item_name} (${displayQuantity} ${displayAcronym})`;

    const cartItem: CartItemSelection = {
      selection_id: editingCartItem?.selection_id || uuidv4(),
      item_id: item.item_id,
      item_name: itemNameWithUnit,
      quantity: quantityInBaseUnit, // Quantity in base unit (grams/ml)
      base_price: item.price,
      selection_groups: editingCartItem?.selection_groups || [],
      modifiers: editingCartItem?.modifiers || [],
      total_price: calculatedPrice,
      comment: editingCartItem?.comment || '',
      clientIndex: clientIndex,
      is_granel: true,
      unidad_medida_id: unitTypeAndOptions.baseUnit, // Store the BASE unit ID (grams=2, ml=5)
      unit_acronym: baseUnit.acronym, // Base unit acronym for inventory tracking
    };

    onAddToCart(cartItem);
    onHide();
  };

  const handleCancel = () => {
    setQuantity('');
    setSelectedUnitId(0);
    setCalculatedPrice(0);
    onHide();
  };

  if (!item) return null;

  const unitTypeAndOptions = getUnitTypeAndOptions(item.unidad_medida_id || 0);
  if (!unitTypeAndOptions) return null;

  const conversions = unitTypeAndOptions.type === 'weight' 
    ? UNIT_CONVERSIONS.weight 
    : UNIT_CONVERSIONS.volume;

  return (
    <Modal show={show} onHide={handleCancel} centered className="granel-modal">
      <Modal.Header>
        <div className="form-modal-title-container">
          <h2 className="form-modal-title">{item.item_name}</h2>
        </div>
      </Modal.Header>
      
      <Modal.Body>
        <div className="granel-content">
          <div className="quantity-section">
            <div className="section-title">
              <FaHashtag className="section-icon" />
              Cantidad
            </div>
            <Form.Control
              type="text"
              placeholder="Cantidad"
              value={quantity}
              onChange={(e) => handleQuantityChange(e.target.value)}
              className="quantity-input"
              autoFocus
            />
          </div>

          <div className="unit-section">
            <div className="section-title">
              <FaRulerHorizontal className="section-icon" />
              Unidad
            </div>
            <div className="unit-buttons">
              {unitTypeAndOptions.options.map((unitId) => {
                const unit = conversions[unitId as keyof typeof conversions];
                return (
                  <Button
                    key={unitId}
                    variant="none"
                    onClick={() => setSelectedUnitId(unitId)}
                    className={`unit-button ${selectedUnitId === unitId ? 'active' : ''}`}
                  >
                    {unit.acronym}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="price-section">
            <div className="section-title">
              <FaDollarSign className="section-icon" />
              Precio calculado
            </div>
            <div className="calculated-price">
              {formatCurrency(calculatedPrice)}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button 
            variant="none"
            className="btn form-button-cancel"
            onClick={handleCancel}
          >
            Cancelar
          </Button>
          <Button 
            variant="none"
            className="btn form-button-create"
            onClick={handleConfirm}
            disabled={!quantity || calculatedPrice <= 0}
          >
            Confirmar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default GranelModal;
